import { AIApi, AiAccess, AiAgentCatalogItem, AiBaseUrl, AiConfigurationRequest, AiIssuedKey, AiKeyMetadata, AiKeyMetadataStatusEnum, AiKeyRevocationResult, CoreFetch } from '../../api';
import { isDemoModeActive } from '../../services/auth/demo-mode.utils';
import isEqual from 'lodash/isEqual';
import { makeAutoObservable, runInAction } from 'mobx';
import {
    cleanConfiguration,
    EMPTY_CONFIGURATION,
    keyStatus,
    stoppedProfiles,
    UUID_PATTERN,
} from '../../services/ai/ai.derive';
import { describeAiError, IAiError, readAiError } from '../../services/ai/ai-error';

export type AiLoadState =
    /** Nothing requested yet. */
    | 'idle'
    | 'loading'
    | 'ready'
    /** The server answered 404 AI_NOT_CONFIGURED. The only way to reach this state. */
    | 'notConfigured'
    /** The request failed; we do not know whether the account is configured (NS-3). */
    | 'error'
    /** Demo mode is on: no `/support/ai` request is made and nothing real may render (NS-14). */
    | 'demo';

export type AiBannerAction = 'reload' | 'retry' | 'overwrite' | 'revoke';

export interface IAiBanner {
    tone: 'info' | 'warning' | 'danger';
    title: string;
    code?: string;
    body?: string;
    actions: Array<{ kind: AiBannerAction; label: string }>;
    /** Profile to preselect in the revoke dialog, for the "issuance stopped" banner. */
    revokeScope?: string;
}

export interface ISaveResult {
    ok: boolean;
    /** The save was held back for a confirmation the UI must show. */
    needsConfirmEmpty?: boolean;
}

export interface ISaveOptions {
    /** Skip the "changed since loaded" check (Overwrite, Retry). */
    force?: boolean;
    /** The operator already confirmed removing every profile. */
    forceEmpty?: boolean;
    /** Save this configuration instead of the draft (used by "Disable & save"). */
    override?: AiConfigurationRequest;
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

const api = (): AIApi => CoreFetch<AIApi>(AIApi);

const PROFILE_ERROR_PATH = /^profiles\[(\d+)\]\.(\w+)/;

/**
 * Which API the page talks to. `support` is the support app: it acts on the granted account and can change its AI
 * policy. `provider` is the provider app: it acts as the signed-in caller, can only see the profiles it has been
 * offered, and can issue, look up and revoke its OWN keys.
 */
export type AiApiMode = 'support' | 'provider';

export interface IAiCapabilities {
    /** Read and change the account's AI policy (the Configuration tab). */
    configure: boolean;
    /** List the gateway's models and registered agents (the Catalog tab). */
    catalog: boolean;
    /** Revoke every key of the account, or of one profile, in one call. */
    bulkRevoke: boolean;
}

const CAPABILITIES: Record<AiApiMode, IAiCapabilities> = {
    support: { configure: true, catalog: true, bulkRevoke: true },
    provider: { configure: false, catalog: false, bulkRevoke: false },
};

/** The calls that differ between `/support/ai…` and `/provider/ai…`; everything else is shared. */
interface IAiClient {
    getAccess(): Promise<AiAccess>;
    issue(request: { profileId: string; lifetimeSeconds?: number }): Promise<AiIssuedKey>;
    getKey(keyId: string): Promise<AiKeyMetadata>;
    revokeKey(keyId: string): Promise<void>;
}

const clientFor = (mode: AiApiMode): IAiClient => {
    const a = api();
    return mode === 'provider'
        ? {
              getAccess: () => a.getProviderAi(),
              issue: aiKeyRequest => a.postProviderAiKey({ aiKeyRequest }),
              getKey: keyId => a.getProviderAiKey({ keyId }),
              revokeKey: keyId => a.deleteProviderAiKey({ keyId }),
          }
        : {
              getAccess: () => a.getSupportAi(),
              issue: aiKeyRequest => a.postSupportAiKey({ aiKeyRequest }),
              getKey: keyId => a.getSupportAiKey({ keyId }),
              revokeKey: keyId => a.deleteSupportAiKey({ keyId }),
          };
};

export class AiStore {
    public loadState: AiLoadState = 'idle';
    public loadError = '';
    public baseUrl?: AiBaseUrl;
    /** The configuration as last read from or written to the server. Undefined when none exists or none is known. */
    public saved?: AiConfigurationRequest;
    public draft?: AiConfigurationRequest;
    /** Undefined means "not loaded" — distinct from an empty catalog (NS-3). */
    public models?: string[];
    public agents?: AiAgentCatalogItem[];
    public catalogErrors: { models?: string; agents?: string } = {};
    public access?: AiAccess;
    public accessError = '';
    /** Keys issued or looked up on this page. There is no list endpoint, so this is never "all keys". */
    public tracked: AiKeyMetadata[] = [];
    /** The key most recently issued on this page. The Testing tab switches to it, so a new key is the one tried. */
    public lastIssuedKeyId?: string;
    public banner?: IAiBanner;
    public syncBlocked = false;
    public saving = false;
    public lookupMessage = '';
    /** 400 field errors from the last save, by profile index and field. Cleared when the draft changes. */
    public serverProfileErrors: Record<number, Record<string, string>> = {};
    public now = Date.now();

    /** What the draft is compared to for the stale-form check: the configuration as it was when we loaded it. */
    private loadedSnapshot?: AiConfigurationRequest;
    private pendingDraft?: AiConfigurationRequest;
    /**
     * Plaintext keys, by key ID. Deliberately not observable (so no devtools or reaction can see them), never
     * persisted and never logged. Cleared by `reset()`.
     */
    private secrets = new Map<string, string>();
    /**
     * Which life of the page an async operation belongs to. `reset()` (page unmount) bumps it, and every operation
     * compares the value it started with after each `await`: one that finds a different number is stale and must not
     * write anything. Without this, an issue or load still in flight when the page closes would land afterwards and
     * put a plaintext key, or account data, back into a store that has just been cleared.
     */
    private generation = 0;

    constructor(public readonly mode: AiApiMode = 'support') {
        makeAutoObservable<AiStore, 'secrets' | 'loadedSnapshot' | 'pendingDraft' | 'generation'>(
            this,
            { secrets: false, loadedSnapshot: false, pendingDraft: false, generation: false },
            { autoBind: true },
        );
    }

    private isStale(generation: number): boolean {
        return generation !== this.generation;
    }

    // ---- derived -------------------------------------------------------------------------------------------

    public get capabilities(): IAiCapabilities {
        return CAPABILITIES[this.mode];
    }

    public get dirty(): boolean {
        return !isEqual(this.draft, this.saved);
    }

    public get sessionKeys(): AiKeyMetadata[] {
        return this.tracked.filter(k => this.secrets.has(k.id));
    }

    public secretFor(keyId: string): string | undefined {
        return this.secrets.get(keyId);
    }

    public statusOf(key: AiKeyMetadata): ReturnType<typeof keyStatus> {
        return keyStatus(key, this.now);
    }

    public tick(): void {
        this.now = Date.now();
    }

    // ---- loading -------------------------------------------------------------------------------------------

    public async load(): Promise<void> {
        if (isDemoModeActive()) {
            this.loadState = 'demo';
            return;
        }
        this.loadState = 'loading';
        this.loadError = '';
        const gen = this.generation;
        if (!this.capabilities.configure) {
            await this.loadAccessOnly(gen);
            return;
        }
        const [config, models, agents] = await Promise.allSettled([
            api().getSupportAiConfiguration(),
            api().getSupportAiModels(),
            api().getSupportAiAgents(),
        ]);

        const errors: AiStore['catalogErrors'] = {};
        let modelList: string[] | undefined;
        let agentList: AiAgentCatalogItem[] | undefined;
        if (models.status === 'fulfilled') modelList = models.value;
        else errors.models = describeAiError(await readAiError(models.reason, 'Could not load models.'));
        if (agents.status === 'fulfilled') agentList = agents.value;
        else errors.agents = describeAiError(await readAiError(agents.reason, 'Could not load agents.'));

        let nextState: AiLoadState = 'ready';
        let nextError = '';
        let cfg: Awaited<ReturnType<AIApi['getSupportAiConfiguration']>> | undefined;
        if (config.status === 'fulfilled') {
            cfg = config.value;
        } else {
            const err = await readAiError(config.reason, 'Could not load the AI configuration.');
            // Only this exact answer means "not configured". Anything else is "we could not find out" (NS-3).
            if (err.status === 404 && err.code === 'AI_NOT_CONFIGURED') {
                nextState = 'notConfigured';
            } else {
                nextState = 'error';
                nextError = describeAiError(err);
            }
        }

        if (this.isStale(gen)) return;
        runInAction(() => {
            this.models = modelList;
            this.agents = agentList;
            this.catalogErrors = errors;
            this.loadState = nextState;
            this.loadError = nextError;
            if (cfg) {
                this.adopt(cfg._configuration, cfg.baseUrl);
            } else {
                this.saved = undefined;
                this.draft = undefined;
                this.loadedSnapshot = undefined;
            }
        });
    }

    /**
     * Provider mode: there is no configuration to read, only what this caller is offered. An account with AI off or no
     * profiles comes back as `enabled: false` and an empty list, not as a 404, so "not configured" is not a state here.
     */
    private async loadAccessOnly(gen: number): Promise<void> {
        try {
            const access = await clientFor(this.mode).getAccess();
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.access = access;
                this.accessError = '';
                this.baseUrl = access.baseUrl;
                this.loadState = 'ready';
            });
        } catch (e) {
            // A failed request is "unknown", never "AI is off" (NS-3).
            const err = await readAiError(e, 'Could not load the available AI profiles.');
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.access = undefined;
                this.loadState = 'error';
                this.loadError = describeAiError(err);
            });
        }
    }

    /** Re-read the saved configuration and discard the draft. */
    public async reload(): Promise<void> {
        this.banner = undefined;
        this.pendingDraft = undefined;
        await this.load();
    }

    public async refreshAgents(): Promise<void> {
        if (!this.capabilities.catalog) return;
        const gen = this.generation;
        try {
            const agents = await api().getSupportAiAgents();
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.agents = agents;
                this.catalogErrors = { ...this.catalogErrors, agents: undefined };
            });
        } catch (e) {
            const err = await readAiError(e, 'Could not load agents.');
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.catalogErrors = { ...this.catalogErrors, agents: describeAiError(err) };
            });
        }
    }

    /** What clients are offered right now: the SAVED configuration, never the draft. */
    public async refreshAccess(): Promise<void> {
        this.accessError = '';
        const gen = this.generation;
        try {
            const access = await clientFor(this.mode).getAccess();
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.access = access;
                if (!this.capabilities.configure) this.baseUrl = access.baseUrl;
            });
        } catch (e) {
            const err = await readAiError(e, 'Could not load available profiles.');
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.access = undefined;
                this.accessError = describeAiError(err);
            });
        }
    }

    private adopt(config: AiConfigurationRequest, baseUrl?: AiBaseUrl, keepDraft = false): void {
        this.saved = clone(config);
        if (!keepDraft) this.draft = clone(config);
        this.loadedSnapshot = clone(config);
        this.serverProfileErrors = {};
        if (baseUrl) this.baseUrl = baseUrl;
    }

    // ---- draft editing -------------------------------------------------------------------------------------

    public createConfiguration(): void {
        if (!this.capabilities.configure) return;
        this.draft = clone(EMPTY_CONFIGURATION);
        this.loadState = 'ready';
    }

    public discard(): void {
        this.serverProfileErrors = {};
        this.pendingDraft = undefined;
        this.dropStaleActions();
        if (this.saved) {
            this.draft = clone(this.saved);
        } else {
            this.draft = undefined;
            this.loadState = 'notConfigured';
        }
    }

    public editDraft(fn: (draft: AiConfigurationRequest) => void): void {
        if (!this.draft || !this.capabilities.configure) return;
        const next = clone(this.draft);
        fn(next);
        this.draft = next;
        this.serverProfileErrors = {};
        // A held-back attempt belongs to the draft it was made from; an edit makes it stale.
        this.pendingDraft = undefined;
        this.dropStaleActions();
    }

    public dismissBanner(): void {
        this.banner = undefined;
    }

    // ---- saving --------------------------------------------------------------------------------------------

    public async save(opts: ISaveOptions = {}): Promise<ISaveResult> {
        if (!this.capabilities.configure) return { ok: false };
        const draft = opts.override ?? this.draft;
        if (!draft || this.saving) return { ok: false };
        const prev = this.saved;

        if (!opts.forceEmpty && prev && prev.profiles.length > 0 && draft.profiles.length === 0) {
            return { ok: false, needsConfirmEmpty: true };
        }

        this.saving = true;
        this.banner = undefined;
        const gen = this.generation;
        // The form stays editable while the request is out. Remember the draft as the save began, so that on success we
        // can tell whether the operator typed anything in the meantime and keep it rather than revert it.
        const draftAtStart = this.draft ? clone(this.draft) : undefined;
        try {
            // The server has no revision to compare. Read again just before replacing so a save made elsewhere
            // after we loaded is shown to the operator instead of silently overwritten.
            if (!opts.force && prev && this.loadedSnapshot) {
                try {
                    const fresh = await api().getSupportAiConfiguration();
                    if (this.isStale(gen)) return { ok: false };
                    // `loadedSnapshot` went through `clone`, which drops keys holding undefined. The generated
                    // *FromJSON returns every absent optional field as such a key, and lodash treats {a: undefined}
                    // and {} as different, so without the same normalisation EVERY configuration looks changed.
                    if (!isEqual(clone(fresh._configuration), this.loadedSnapshot)) {
                        runInAction(() => {
                            this.pendingDraft = draft;
                            this.banner = {
                                tone: 'warning',
                                title: 'Configuration changed since this page loaded it.',
                                body: 'Another save completed after the last read. Saving replaces the current policy in full. Reload to review, or overwrite intentionally.',
                                actions: [
                                    { kind: 'reload', label: 'Reload saved' },
                                    { kind: 'overwrite', label: 'Overwrite' },
                                ],
                            };
                        });
                        return { ok: false };
                    }
                } catch (e) {
                    const err = await readAiError(e, 'Could not check the saved configuration.');
                    if (this.isStale(gen)) return { ok: false };
                    this.setFailure(err, draft);
                    return { ok: false };
                }
            } else if (!opts.force && !prev) {
                // Creating. This page found the account unconfigured, but a second operator may have created the
                // policy since, and a PUT is a full replacement. Re-read, and go ahead only on the exact answer that
                // means "still nothing there"; a configuration that now exists is a conflict, and any other failure
                // leaves the account's state unknown (NS-3), so nothing is created.
                try {
                    await api().getSupportAiConfiguration();
                    if (this.isStale(gen)) return { ok: false };
                    runInAction(() => {
                        this.pendingDraft = draft;
                        this.banner = {
                            tone: 'warning',
                            title: 'This account has been configured since this page loaded.',
                            body: 'Another save created a configuration while you were editing. Saving replaces it in full. Reload to review it, or overwrite intentionally.',
                            actions: [
                                { kind: 'reload', label: 'Reload saved' },
                                { kind: 'overwrite', label: 'Overwrite' },
                            ],
                        };
                    });
                    return { ok: false };
                } catch (e) {
                    const err = await readAiError(e, 'Could not check whether the account is configured.');
                    if (this.isStale(gen)) return { ok: false };
                    if (!(err.status === 404 && err.code === 'AI_NOT_CONFIGURED')) {
                        this.setFailure(err, draft);
                        return { ok: false };
                    }
                    // Still not configured: safe to create.
                }
            }

            const saved = await api().putSupportAiConfiguration({ aiConfigurationRequest: cleanConfiguration(draft) });
            // The server took the save, but the page that asked for it is gone: there is nothing left to update.
            if (this.isStale(gen)) return { ok: false };
            const stopped = stoppedProfiles(prev, saved._configuration);
            runInAction(() => {
                // What the server now holds always becomes the saved copy. The DRAFT is replaced with it only if it is
                // still what was sent; if the operator edited during the request, their newer edit stays, and the form
                // shows it as unsaved (it differs from the saved copy) instead of silently reverting it to a clean state.
                const editedDuringSave = !isEqual(this.draft, draftAtStart);
                this.adopt(saved._configuration, saved.baseUrl, editedDuringSave);
                this.loadState = 'ready';
                this.syncBlocked = false;
                this.pendingDraft = undefined;
                this.banner = stopped.length
                    ? {
                          tone: 'info',
                          title: `Issuance stopped for ${stopped.join(', ')}.`,
                          body: 'Existing keys keep their issued policy until expiry. Revoke them to stop access now.',
                          actions: [{ kind: 'revoke', label: 'Revoke keys…' }],
                          revokeScope: stopped.length === 1 && saved._configuration.enabled ? stopped[0] : undefined,
                      }
                    : undefined;
            });
            return { ok: true };
        } catch (e) {
            const err = await readAiError(e, 'Could not save the AI configuration.');
            if (this.isStale(gen)) return { ok: false };
            this.setFailure(err, draft);
            return { ok: false };
        } finally {
            // A stale save leaves `saving` alone: reset() already cleared it, and a newer visit may have its own.
            if (!this.isStale(gen)) {
                runInAction(() => {
                    this.saving = false;
                });
            }
        }
    }

    /**
     * Switch issuance off for the whole account, or for one profile, and save that. Used by "Disable & save" in the
     * revoke dialog.
     *
     * It saves the SAVED configuration with one change, and a successful save replaces the draft with what was saved.
     * So with unsaved edits in the draft it would silently throw them away; it refuses instead, and the caller says
     * why (`blockedByDraft`). The operator saves or discards first, then disables.
     */
    public async disableIssuance(profileId?: string): Promise<ISaveResult & { blockedByDraft?: boolean }> {
        if (this.dirty) return { ok: false, blockedByDraft: true };
        if (!this.saved) return { ok: false };
        const next = clone(this.saved);
        if (profileId) {
            next.profiles.forEach(p => {
                if (p.id === profileId) p.enabled = false;
            });
        } else {
            next.enabled = false;
        }
        return this.save({ override: next, forceEmpty: true });
    }

    public async bannerAction(kind: AiBannerAction): Promise<void> {
        if (kind === 'reload') await this.reload();
        else if (kind === 'retry' || kind === 'overwrite') {
            // The draft the failed attempt carried, not whatever the form holds now.
            const held = this.pendingDraft;
            if (!held) {
                // The operator has edited or discarded since, which dropped the attempt this button belonged to. Do not
                // fall back to the current draft: `save` would send it, and with `forceEmpty` it could even remove
                // every profile without the usual confirmation. A stale action does nothing, and goes away.
                this.banner = undefined;
                return;
            }
            await this.save({ force: true, forceEmpty: true, override: held });
        }
        // 'revoke' opens a dialog; the page handles it.
    }

    /**
     * Retry and Overwrite act on a held-back attempt. When that attempt is dropped (the draft was edited or discarded)
     * their buttons are stale, so they leave the banner at once instead of waiting to be clicked and refused.
     */
    private dropStaleActions(): void {
        const banner = this.banner;
        if (!banner || !banner.actions.some(a => a.kind === 'retry' || a.kind === 'overwrite')) return;
        this.banner = { ...banner, actions: banner.actions.filter(a => a.kind !== 'retry' && a.kind !== 'overwrite') };
    }

    private setFailure(err: IAiError, draft: AiConfigurationRequest): void {
        runInAction(() => {
            this.pendingDraft = draft;
            if (err.status === 400 && err.fieldErrors) {
                const byProfile: Record<number, Record<string, string>> = {};
                const rest: string[] = [];
                for (const [path, message] of Object.entries(err.fieldErrors)) {
                    const m = PROFILE_ERROR_PATH.exec(path);
                    if (m) {
                        const idx = Number(m[1]);
                        byProfile[idx] = { ...(byProfile[idx] ?? {}), [m[2]]: message };
                    } else {
                        rest.push(`${path}: ${message}`);
                    }
                }
                this.serverProfileErrors = byProfile;
                this.banner = {
                    tone: 'danger',
                    title: 'The server rejected the configuration.',
                    code: '400',
                    body: rest.length ? rest.join('; ') : 'See the highlighted profile fields.',
                    actions: [],
                };
            } else if (err.code === 'AI_AGENT_UNAVAILABLE') {
                this.banner = {
                    tone: 'danger',
                    title: 'An agent is unavailable.',
                    code: '409 AI_AGENT_UNAVAILABLE',
                    body: err.message,
                    actions: [],
                };
                void this.refreshAgents();
            } else if (err.code === 'AI_CONFIGURATION_CONFLICT') {
                this.banner = {
                    tone: 'danger',
                    title: 'Configuration changed during save.',
                    code: '409 AI_CONFIGURATION_CONFLICT',
                    body: 'A competing save finished first. Reload the saved configuration and re-apply the intended policy.',
                    actions: [{ kind: 'reload', label: 'Reload saved' }],
                };
            } else if (err.status === 503) {
                this.syncBlocked = true;
                this.banner = {
                    tone: 'danger',
                    title: 'Gateway synchronization failed. New issuance is blocked.',
                    code: '503 AI_GATEWAY_UNAVAILABLE',
                    body: 'Saved policy and gateway account limits are not synchronized. Issuance stays blocked until a save succeeds.',
                    actions: [{ kind: 'retry', label: 'Retry save' }],
                };
            } else {
                this.banner = {
                    tone: 'danger',
                    title: 'Could not save the configuration.',
                    code: err.status ? `${err.status}${err.code ? ' ' + err.code : ''}` : undefined,
                    body: `${err.message}${err.traceId ? ` Trace ${err.traceId}.` : ''}`,
                    actions: [],
                };
            }
        });
    }

    // ---- keys ----------------------------------------------------------------------------------------------

    /**
     * Issue a key. NOT idempotent: each call creates a distinct key, so this is never retried automatically and
     * the caller must say so when it fails.
     */
    public async issueKey(profileId: string, lifetimeSeconds?: number): Promise<{ key?: AiIssuedKey; error?: IAiError }> {
        const gen = this.generation;
        try {
            const key = await clientFor(this.mode).issue({ profileId, ...(lifetimeSeconds ? { lifetimeSeconds } : {}) });
            if (this.isStale(gen)) {
                // The page closed while the request was in flight, and `reset()` has already cleared the secrets. Storing
                // this one now would put a plaintext key back into a store that was just emptied. Nobody can ever be
                // shown it, so it is a live credential with no owner: ask for it to be revoked (best effort) and drop it.
                void clientFor(this.mode)
                    .revokeKey(key.id)
                    .catch(() => undefined);
                return { error: { message: 'The page was closed before the key arrived, so it was discarded and a revocation was requested.' } };
            }
            runInAction(() => {
                this.secrets.set(key.id, key.apiKey);
                this.upsertTracked(this.metadataOf(key));
                this.lastIssuedKeyId = key.id;
            });
            return { key };
        } catch (e) {
            return { error: await readAiError(e, 'Could not issue a key.') };
        }
    }

    public async lookupKey(rawId: string): Promise<void> {
        const id = rawId.trim().toLowerCase();
        if (!UUID_PATTERN.test(id)) {
            this.lookupMessage = 'The key ID must be a UUID (400). Never paste the plaintext key.';
            return;
        }
        const gen = this.generation;
        try {
            const key = await clientFor(this.mode).getKey(id);
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.lookupMessage = '';
                this.upsertTracked(key);
            });
        } catch (e) {
            const err = await readAiError(e, 'Could not look up the key.');
            if (this.isStale(gen)) return;
            runInAction(() => {
                this.lookupMessage = describeAiError(err);
            });
        }
    }

    public clearLookupMessage(): void {
        this.lookupMessage = '';
    }

    /** Revoke one key. Safe to retry after a 503. */
    public async revokeKey(keyId: string): Promise<{ ok: boolean; error?: IAiError }> {
        const gen = this.generation;
        try {
            await clientFor(this.mode).revokeKey(keyId);
            // Revoked on the server; if the page has gone there is no list left to update.
            if (this.isStale(gen)) return { ok: true };
            runInAction(() => {
                this.tracked = this.tracked.map(k => (k.id === keyId ? { ...k, status: AiKeyMetadataStatusEnum.REVOKED } : k));
            });
            return { ok: true };
        } catch (e) {
            const err = await readAiError(e, 'Could not revoke the key.');
            if (this.isStale(gen)) return { ok: false, error: err };
            // Every failure is shown. This screen turns the app's global error dialog off, so a 403, a 404 or a dropped
            // connection would otherwise leave the operator believing a live credential had been revoked.
            runInAction(() => {
                this.banner =
                    err.status === 503
                        ? {
                              tone: 'danger',
                              title: `Revocation unconfirmed for key ${keyId.slice(0, 8)}.`,
                              code: '503 AI_GATEWAY_UNAVAILABLE',
                              body: 'The gateway did not confirm revocation. Retrying is safe.',
                              actions: [],
                          }
                        : {
                              tone: 'danger',
                              title: `Key ${keyId.slice(0, 8)} was not revoked.`,
                              body: `${describeAiError(err)} The key may still be valid.`,
                              actions: [],
                          };
            });
            return { ok: false, error: err };
        }
    }

    /** Revoke every unexpired key for the account, or one profile's. Safe to retry; a 503 may be partial. */
    public async revokeKeys(profileId?: string): Promise<{ result?: AiKeyRevocationResult; error?: IAiError }> {
        if (!this.capabilities.bulkRevoke) {
            // The provider API has no bulk revocation, and there is no safe stand-in for it.
            return { error: { message: 'Revoking several keys at once is not available here.' } };
        }
        if (this.saving) {
            // "Disable & save" is usually what brought the operator here. Revoking before that save lands would take the
            // cutoff while issuance is still on, and a key issued in the gap would outlive the revocation.
            return {
                error: {
                    message:
                        'A configuration save is still in progress. Revoke once it has finished, so issuance is already off when the revocation cutoff is taken.',
                },
            };
        }
        try {
            const result = await api().deleteSupportAiKeys({ profileId: profileId || undefined });
            await this.refreshTracked();
            return { result };
        } catch (e) {
            return { error: await readAiError(e, 'Could not revoke keys.') };
        }
    }

    /** Re-read each tracked key, since a bulk revocation changes keys we did not name. */
    public async refreshTracked(): Promise<void> {
        const gen = this.generation;
        const ids = this.tracked.map(k => k.id);
        const client = clientFor(this.mode);
        const results = await Promise.allSettled(ids.map(keyId => client.getKey(keyId)));
        if (this.isStale(gen)) return;
        runInAction(() => {
            results.forEach(r => {
                if (r.status === 'fulfilled') this.upsertTracked(r.value);
            });
        });
    }

    private upsertTracked(key: AiKeyMetadata): void {
        const rest = this.tracked.filter(k => k.id !== key.id);
        this.tracked = [key, ...rest];
    }

    /** Metadata only — the issued key's secret and expiry-in-seconds are not part of what we keep in state. */
    private metadataOf(key: AiIssuedKey): AiKeyMetadata {
        const { apiKey: _apiKey, expiresInSeconds: _expires, ...meta } = key as AiIssuedKey & Record<string, unknown>;
        return meta as unknown as AiKeyMetadata;
    }

    // ---- lifecycle -----------------------------------------------------------------------------------------

    /**
     * Drop everything, including plaintext keys, and invalidate every operation still in flight (see `generation`).
     * Called when the page unmounts.
     */
    public reset(): void {
        this.generation += 1;
        this.secrets.clear();
        this.loadState = 'idle';
        this.loadError = '';
        this.baseUrl = undefined;
        this.saved = undefined;
        this.draft = undefined;
        this.loadedSnapshot = undefined;
        this.pendingDraft = undefined;
        this.models = undefined;
        this.agents = undefined;
        this.catalogErrors = {};
        this.access = undefined;
        this.accessError = '';
        this.tracked = [];
        this.lastIssuedKeyId = undefined;
        this.banner = undefined;
        this.syncBlocked = false;
        this.saving = false;
        this.lookupMessage = '';
        this.serverProfileErrors = {};
    }
}

/** `support` for the support app, `provider` for the provider app. */
export function createAiStore(mode: AiApiMode): AiStore {
    return new AiStore(mode);
}
